import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalContent } from '@nextui-org/react';

// Custom hook for media query with optimized dependencies
const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(() => {
    // Initialize with the current match state if window is available
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const media = window.matchMedia(query);
    setMatches(media.matches);

    // Modern event listener approach
    const listener = (e: MediaQueryListEvent) => setMatches(e.matches);

    // Use modern addEventListener if available, fallback to addListener
    if (media.addEventListener) {
      media.addEventListener('change', listener);
      return () => media.removeEventListener('change', listener);
    } else {
      // Fallback for older browsers
      media.addListener(listener);
      return () => media.removeListener(listener);
    }
  }, [query]); // Remove matches from dependencies

  return matches;
};

interface ModalSlideFilterProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
}

// Memoize motion variants to prevent recreating on each render
const motionVariants = {
  enter: {
    x: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
  exit: {
    x: -500,
    transition: {
      duration: 0.4,
      ease: 'easeOut',
    },
  },
};

// Memoize modal class names
const modalClassNames = {
  base: 'justify-start m-0 p-0 h-dvh max-h-full w-[300px] z-[50]',
  wrapper: 'items-start justify-start !w-[300px]',
  body: 'p-0',
  closeButton: 'z-50',
} as const;

const ModalSlideFilter = React.memo(
  ({ children, isOpen, onClose }: ModalSlideFilterProps) => {
    const isMd = useMediaQuery('(min-width: 768px)');

    const desktopStyle: React.CSSProperties = {
      width: isOpen ? 256 : 0,
      minWidth: isOpen ? 256 : 0,
      flexBasis: isOpen ? 256 : 0,
      opacity: isOpen ? 1 : 0,
      pointerEvents: isOpen ? 'auto' : 'none',
    };

    if (isMd) {
      return (
        <div
          className='h-screen bg-background shadow-md transition-all duration-300 z-30 relative shrink-0 overflow-hidden'
          style={desktopStyle}
        >
          <div className='h-full w-64'>{children}</div>
        </div>
      );
    }
    const handleContentClick = (e: React.MouseEvent) => {
      // Prevenir que el clic se propague al contenedor padre
      e.stopPropagation();
    };

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            onClose();
          }
        }}
        classNames={modalClassNames}
        motionProps={{
          variants: motionVariants,
        }}
        radius='none'
        scrollBehavior='inside'
        isDismissable={false}
      >
        <ModalContent>
          <ModalBody onClick={handleContentClick}>{children}</ModalBody>
        </ModalContent>
      </Modal>
    );
  }
);

// Add display name for debugging
ModalSlideFilter.displayName = 'ModalSlideFilter';

export default ModalSlideFilter;
